import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import ArticleRewardListing from '../../components/Register/ArticleReward/ArticleRewardListing';
import ArticleRewardForm from '../../components/Register/ArticleReward/ArticleRewardForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const ArticleReward = () => {
    const toast = useRef(null);
    const today = new Date().toISOString().split('T')[0];

    const initialForm = { Id: 0, Note: '', ArticleIds: [], ArticleIdsJson: '[]', ArticleCount: 0, TotalAmount: 0, Status: 'Lưu nháp' };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
    const userRoleName = (currentUser?.RoleName || '').toLowerCase();
    const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
                handleLogout();
                if (!isBackground) setIsLoading(false);
                return;
            }
            const currentUser = JSON.parse(userStr);

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/articlereward?userId=${currentUser.Id}&isAdmin=false`,
                {
                    method: 'GET',
                    headers: getAuthHeaders()
                }
            );

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
                setFilteredData(result);
            }
        } catch (error) {
            console.error("Lỗi lấy dữ liệu:", error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu!' });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const query = searchQuery.toLowerCase();
        const filtered = data.filter(item =>
            (item.Note && item.Note.toLowerCase().includes(query)) ||
            (item.Status && item.Status.toLowerCase().includes(query))
        );
        setFilteredData(filtered);
    }, [searchQuery, data]);

    const handleEdit = (item) => {
        setFormData({ ...item });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);

        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Hồ sơ đã khóa!', life: 3000 });
            return;
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa đăng ký khen thưởng này?',
            accept: async () => {
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user'));
                    if (!currentUser) return handleLogout();

                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?id=${id}&userId=${currentUser.Id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (response.status === 401 || response.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (response.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa!', life: 3000 });
                        fetchData(true);
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại!' });
                    }
                } catch (error) {
                    console.error("Lỗi xóa:", error);
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} đăng ký khen thưởng đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user'));
                    if (!currentUser) return handleLogout();

                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?id=${id}&userId=${currentUser.Id}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser) return handleLogout();

            const userId = currentUser.Id;
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?isAdmin=${isManager}&userId=${userId}`, {
                method: 'PATCH',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id, status: newStatus })
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? 'Gửi yêu cầu duyệt thành công!' : 'Hủy yêu cầu duyệt thành công!',
                    life: 3000
                });
                fetchData(true);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Cập nhật trạng thái thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleBulkUpdateStatus = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Lưu nháp') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser) return handleLogout();

            const userId = currentUser.Id;
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?isAdmin=${isManager}&userId=${userId}`, {
                    method: 'PATCH',
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id, status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData(initialForm);
    };

    const getCurrentUser = () => {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    };

    const exportToExcel = () => {
        const dataToExport = filteredData;

        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const excelData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Ghi chú hồ sơ': item.Note || 'Không có ghi chú',
            'Số lượng bài báo': item.ArticleCount || 0,
            'Tổng tiền đề xuất (VNĐ)': item.TotalAmount || 0,
            'Trạng thái': item.Status || 'Lưu nháp',
            'Ngày tạo': item.CreatedDate ? new Date(item.CreatedDate).toLocaleDateString('vi-VN') : '',
            'Người tạo': item.Email || ''
        }));

        excelData.push({
            'STT': '',
            'Ghi chú hồ sơ': 'TỔNG CỘNG:',
            'Số lượng bài báo': dataToExport.reduce((sum, item) => sum + (item.ArticleCount || 0), 0),
            'Tổng tiền đề xuất (VNĐ)': dataToExport.reduce((sum, item) => sum + (item.TotalAmount || 0), 0),
            'Trạng thái': '',
            'Ngày tạo': '',
            'Người tạo': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "XetThuongBaiBao");

        const wscols = [
            { wch: 5 },
            { wch: 50 },
            { wch: 20 },
            { wch: 25 },
            { wch: 20 },
            { wch: 15 },
            { wch: 25 }
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `DanhSachXetThuong_${today}.xlsx`);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>HỖ TRỢ CÔNG BỐ KHOA HỌC</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button
                    className="btn-add-new"
                    onClick={() => { setFormData(initialForm); setIsModalOpen(true); }}
                    style={{ backgroundColor: '#0056b3', margin: 0 }}
                >
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '5px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <p className="sub-title" style={{ margin: 0, fontWeight: 'bold' }}>HỒ SƠ CỦA TÔI</p>
                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input
                            type="text"
                            placeholder="Tìm kiếm"
                            className="form-input"
                            style={{ width: '100%', paddingLeft: '35px' }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <ArticleRewardListing
                data={filteredData}
                searchQuery={searchQuery}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleUpdateStatus(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleUpdateStatus(id, 'Lưu nháp')}
                onBulkSend={(ids) => handleBulkUpdateStatus(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkUpdateStatus(ids, 'Lưu nháp')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
            />

            {getCurrentUser() && (
                <ArticleRewardForm
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    onSuccess={() => { fetchData(true); closeModal(); }}
                    formData={formData}
                    setFormData={setFormData}
                    user={getCurrentUser()}
                    allRequests={data}
                />
            )}
        </div>
    );
};

export default ArticleReward;